import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { soundManager } from '../../utils/sounds';
import { http } from '../../api/http';

// Types matching server response
type TournamentFormat = 'swiss' | 'single_elim' | 'double_elim';
type TournamentStatus = 'scheduled' | 'registration' | 'in_progress' | 'completed' | 'cancelled';
type TournamentMode = 'daily_quick' | 'weekly' | 'high_roller';

interface Tournament {
  id: string;
  name: string;
  description?: string;
  format: TournamentFormat;
  mode: TournamentMode;
  status: TournamentStatus;
  maxPlayers: number;
  minPlayers: number;
  registeredCount: number;
  rounds: number;
  currentRound: number;
  prizeType: 'xp' | 'sol';
  entryFeeLamports: number;
  prizePoolLamports: number;
  xpPrizes: Record<number, number>;
  registrationStart: string;
  registrationEnd: string;
  startTime: string;
  isRegistered?: boolean;
}

interface TournamentsResponse {
  tournaments: Tournament[];
}

// Time formatting
function formatTimeUntil(isoTime: string): string {
  const now = new Date();
  const target = new Date(isoTime);
  const diff = target.getTime() - now.getTime();

  if (diff <= 0) return 'Now';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

// Format config for modes
const MODE_CONFIG: Record<TournamentMode, { label: string; color: string }> = {
  daily_quick: { label: 'DAILY', color: 'text-arcade-cyan' },
  weekly: { label: 'WEEKLY', color: 'text-arcade-orange' },
  high_roller: { label: 'HIGH ROLLER', color: 'text-arcade-pink' },
};

const FORMAT_CONFIG: Record<TournamentFormat, { label: string; icon: string }> = {
  swiss: { label: 'Swiss', icon: '🔄' },
  single_elim: { label: 'Single Elim', icon: '⚔️' },
  double_elim: { label: 'Double Elim', icon: '🏆' },
};

// Single tournament card
export const TournamentCard: React.FC<{
  tournament: Tournament;
  onRegister: () => void;
  wallet?: string;
}> = ({ tournament, onRegister, wallet }) => {
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(tournament.isRegistered || false);
  const [timeUntil, setTimeUntil] = useState(formatTimeUntil(tournament.startTime));

  const modeConfig = MODE_CONFIG[tournament.mode] || {
    label: tournament.mode,
    color: 'text-white',
  };
  const formatConfig = FORMAT_CONFIG[tournament.format] || { label: tournament.format, icon: '🎮' };
  const spotsLeft = tournament.maxPlayers - tournament.registeredCount;
  const isFull = spotsLeft <= 0;
  const canRegister =
    wallet &&
    (tournament.status === 'scheduled' || tournament.status === 'registration') &&
    !registered &&
    !isFull;

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeUntil(formatTimeUntil(tournament.startTime));
    }, 60000);
    return () => clearInterval(interval);
  }, [tournament.startTime]);

  useEffect(() => {
    setRegistered(tournament.isRegistered || false);
  }, [tournament.isRegistered]);

  const handleRegister = async () => {
    if (!canRegister || registering || !wallet) return;

    setRegistering(true);
    try {
      await http.post<{ success: boolean }>(`tournaments/${tournament.id}/register`, { wallet });
      setRegistered(true);
      soundManager.play('achievement');
      onRegister();
    } catch (err) {
      console.error('Failed to register:', err);
    } finally {
      setRegistering(false);
    }
  };

  // Calculate total XP prize
  const totalXpPrize = tournament.xpPrizes
    ? Object.values(tournament.xpPrizes).reduce((sum, xp) => sum + xp, 0)
    : 0;
  const prizeDisplay =
    tournament.prizeType === 'sol'
      ? `${(tournament.prizePoolLamports / 1e9).toFixed(2)} SOL`
      : `${totalXpPrize} XP`;

  return (
    <div className="arcade-panel pixel-corners rounded-xl p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display text-sm font-bold text-white">{tournament.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs font-bold ${modeConfig.color}`}>{modeConfig.label}</span>
            <span className="text-white/30">•</span>
            <span className="text-xs text-white/50">
              {formatConfig.icon} {formatConfig.label} ({tournament.rounds} rounds)
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-white/40">Starts in</div>
          <div className="font-mono text-sm text-arcade-yellow">{timeUntil}</div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-white/5 p-2">
          <div className="text-xs text-white/40">PLAYERS</div>
          <div className="font-display text-sm text-white">
            {tournament.registeredCount}/{tournament.maxPlayers}
          </div>
        </div>
        <div className="rounded-lg bg-white/5 p-2">
          <div className="text-xs text-white/40">PRIZE</div>
          <div className="font-display text-sm text-arcade-yellow">{prizeDisplay}</div>
        </div>
        <div className="rounded-lg bg-white/5 p-2">
          <div className="text-xs text-white/40">SPOTS LEFT</div>
          <div
            className={`font-display text-sm ${spotsLeft <= 5 ? 'text-red-400' : 'text-arcade-green'}`}
          >
            {spotsLeft}
          </div>
        </div>
      </div>

      {/* Entry fee for SOL tournaments */}
      {tournament.prizeType === 'sol' && tournament.entryFeeLamports > 0 && (
        <div className="text-center text-xs text-white/50">
          Entry fee: {(tournament.entryFeeLamports / 1e9).toFixed(3)} SOL
        </div>
      )}

      {/* Action */}
      <div className="flex gap-2">
        {!wallet ? (
          <div className="flex-1 flex items-center justify-center rounded-lg border border-white/20 bg-white/5 py-2 text-sm text-white/50">
            Connect wallet to register
          </div>
        ) : registered ? (
          <div className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-arcade-green/50 bg-arcade-green/20 py-2 text-sm text-arcade-green">
            <span>✓</span>
            <span className="font-display">REGISTERED</span>
          </div>
        ) : canRegister ? (
          <button
            onClick={handleRegister}
            disabled={registering}
            className="arcade-btn flex-1 py-2 text-xs disabled:opacity-50"
          >
            {registering ? 'REGISTERING...' : 'REGISTER NOW'}
          </button>
        ) : isFull ? (
          <div className="flex-1 flex items-center justify-center rounded-lg border border-white/20 bg-white/5 py-2 text-sm text-white/50">
            FULL
          </div>
        ) : tournament.status === 'in_progress' ? (
          <div className="flex-1 flex items-center justify-center rounded-lg border border-arcade-yellow/50 bg-arcade-yellow/10 py-2 text-sm text-arcade-yellow">
            IN PROGRESS
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center rounded-lg border border-white/20 bg-white/5 py-2 text-sm text-white/50">
            {tournament.status.toUpperCase()}
          </div>
        )}
        <Link
          to={`/tournaments/${tournament.id}`}
          className="rounded-lg border border-white/20 bg-white/5 px-4 py-2 text-xs text-white/70 hover:bg-white/10 transition"
        >
          Details →
        </Link>
      </div>
    </div>
  );
};

// Main TournamentBanner component
export const TournamentBanner: React.FC = () => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTournaments = useCallback(async () => {
    try {
      const params = wallet ? `?wallet=${wallet}` : '';
      const response = await http.get<TournamentsResponse>(`tournaments${params}`);
      // Filter to only show upcoming or registration tournaments
      const upcoming = response.tournaments.filter(
        (t) =>
          t.status === 'scheduled' || t.status === 'registration' || t.status === 'in_progress',
      );
      setTournaments(upcoming);
    } catch (err) {
      console.error('Failed to fetch tournaments:', err);
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  const handleRegister = () => {
    fetchTournaments();
  };

  if (loading) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="arcade-panel pixel-corners rounded-2xl p-4 border border-arcade-purple/30 bg-arcade-purple/10">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏆</span>
            <div className="text-white/50 animate-pulse">Loading tournaments...</div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (tournaments.length === 0) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="arcade-panel pixel-corners rounded-2xl p-4 border border-arcade-purple/30 bg-arcade-purple/10">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🏆</span>
              <div>
                <h2 className="font-display text-sm font-bold text-arcade-purple">TOURNAMENTS</h2>
                <div className="text-xs text-white/50">No tournaments scheduled</div>
              </div>
            </div>
            <Link to="/tournaments" className="arcade-btn px-4 py-2 text-xs">
              VIEW ALL
            </Link>
          </div>
        </div>
      </motion.div>
    );
  }

  const nextTournament = tournaments[0];
  const totalXpPrize = nextTournament.xpPrizes
    ? Object.values(nextTournament.xpPrizes).reduce((sum, xp) => sum + xp, 0)
    : 0;
  const prizeDisplay =
    nextTournament.prizeType === 'sol'
      ? `${(nextTournament.prizePoolLamports / 1e9).toFixed(2)} SOL`
      : `${totalXpPrize} XP`;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      {/* Compact banner for homepage */}
      <div className="arcade-panel pixel-corners rounded-2xl p-4 border border-arcade-purple/30 bg-arcade-purple/10">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏆</span>
            <div>
              <h2 className="font-display text-sm font-bold text-arcade-purple">
                {nextTournament.name.toUpperCase()}
              </h2>
              <div className="text-xs text-white/50">
                {nextTournament.registeredCount} players registered • {prizeDisplay} prize
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs text-white/40">
                {nextTournament.status === 'in_progress' ? 'Round' : 'Starts in'}
              </div>
              <div className="font-mono text-lg text-arcade-yellow">
                {nextTournament.status === 'in_progress'
                  ? `${nextTournament.currentRound}/${nextTournament.rounds}`
                  : formatTimeUntil(nextTournament.startTime)}
              </div>
            </div>
            <Link to="/tournaments" className="arcade-btn px-4 py-2 text-xs">
              VIEW
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Export types for other components
export type { Tournament, TournamentFormat, TournamentStatus, TournamentMode };
